import React, {useState} from 'react';
import {Field, Form, Formik} from "formik";
import {Box, Checkbox, List, ListItem, ListItemText} from "@material-ui/core";
import * as Yup from "yup";
import TextFieldWithErrorMessage from "../form/TextFieldWithErrorMessage";
import ComboboxWithErrorMessage from "../form/ComboboxWithErrorMessage";
import {addComment, getPartyTypes} from "../../api/CommentEndpoint";
import {useNavigate, useLocation, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {RR_STATUS} from "../../constants/RRConstants";
import {useSnackbar} from "notistack";
import Loader from "../core/Loader";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import queryString from 'query-string';
import {COMMUNICATOR_PAGE_PATH} from "../../layouts/navigation/Navigator";
import { normalizeToString } from '../../utils/QueryParamNormalizerUtils';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        list: {
            overflow: "auto",
            height: 200,
            border: "1px solid lightgray",
            borderRadius: "4px"
        }
    })
);

const validationSchema = Yup.object({
    text: Yup.string()
        .max(1000, "form.comment.error.max-length")
        .required("form.comment.error.message-required", true),
    permissions: Yup.string()
});

interface IPartyType {
    type: string,
    preselected: boolean,
    changeable: boolean
}

const CommentPermissionGroup = {
    ALL_PARTIES: "form.comment.permission-group.all-parties",
    STAFF: "form.comment.permission-group.staff",
    AGENT_ONLY: "form.comment.permission-group.agent-only"
}

type PartyTypesListProps = {
    partyTypes: IPartyType[],
    setSelected: (partyType: IPartyType, selected: boolean) => void
}

const PartyTypesList: React.FC<PartyTypesListProps> = ({partyTypes, setSelected}) => {
    const classes = useStyles();

    return (
        <Box pb={2}>
            <List dense className={classes.list}>
                {
                    (partyTypes || []).map(partyType => {
                        let {type, preselected, changeable} = partyType;

                        return <ListItem button disabled={!changeable} selected={preselected} key={type}
                                         onClick={() => {
                                             if (changeable) {
                                                 setSelected(partyType, !preselected);
                                             }
                                         }}>
                            <Checkbox color="primary" size="small" disabled={!changeable} checked={preselected}/>
                            <ListItemText primary={type}/>
                        </ListItem>
                    })
                }

            </List>
        </Box>
    )
};


const CommentForm: React.FC<{ formRef: any }> = ({formRef}) => {

    const {shipmentId} = useParams();
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const {redirect} = queryString.parse(location.search);

    const [partyTypes, setPartyTypes] = useState<IPartyType[] | undefined>(undefined);
    const [groupToPartyTypesMap] = useState<Map<string, IPartyType[] | undefined>>(new Map());

    React.useEffect(() => {
        Object.keys(CommentPermissionGroup).forEach(
            group => {
                getPartyTypes(shipmentId, group).then(({data}) => {
                    const {status, response} = data;
                    if (status !== RR_STATUS.OK) {
                        enqueueSnackbar(t("core.server-error"), {
                            variant: "error",
                            preventDuplicate: false
                        });

                        groupToPartyTypesMap.set(group, undefined);

                    } else {
                        groupToPartyTypesMap.set(group, response);

                        //set default values
                        if (group === "ALL_PARTIES") {
                            setPartyTypes(response);
                        }
                    }
                });
            }
        );
    }, [enqueueSnackbar, groupToPartyTypesMap, shipmentId, t]);

    const handlePermissionSetChange = (event) => {
        setPartyTypes(groupToPartyTypesMap.get(event.target.value));
    };

    const updateSelected = (partyTypeToUpdate: IPartyType, select: boolean) => {
        setPartyTypes(partyTypes => {
            return (partyTypes || []).map((partyType) => {
                if (partyType === partyTypeToUpdate) {
                    return {...partyType, preselected: select};
                } else {
                    return partyType;
                }
            })
        })
    };

    const getSelectedPartyTypes = () => {
        return partyTypes?.filter(partyType => partyType.preselected).map(partyType => partyType.type);
    };

    return (
        <Formik
            initialValues={{
                text: "",
                permissions: "ALL_PARTIES"
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {

                return addComment(shipmentId, {
                    ...values,
                    shipmentId: shipmentId,
                    customizedPermissions: getSelectedPartyTypes()
                }).then(({data}) => {
                    const {status} = data;

                    if (status !== RR_STATUS.OK) {
                        enqueueSnackbar(t("form.comment.error.add-comment-failed"), {
                            variant: "error",
                            preventDuplicate: false
                        });
                    } else {
                        enqueueSnackbar(t("form.comment.submission-success"), {
                            variant: "success",
                            preventDuplicate: false
                        });

                        navigate(normalizeToString(redirect) ?? COMMUNICATOR_PAGE_PATH);
                    }

                })

            }}
            innerRef={formRef}
        >
            {() => (
                <Form>
                    <Field component={TextFieldWithErrorMessage} name="text" required variant="outlined" size="small"
                           multiline rows={5}/>
                    <Field component={ComboboxWithErrorMessage} name="permissions"
                           caption="form.comment.permission-set"
                           variant="outlined" size="small" onChange={handlePermissionSetChange}>
                        {Object.keys(CommentPermissionGroup).map(group => (
                            <option key={group} value={group}>
                                {t(CommentPermissionGroup[group])}
                            </option>
                        ))}
                    </Field>

                    {partyTypes ? <PartyTypesList partyTypes={partyTypes} setSelected={updateSelected}/> : <Loader/>}

                </Form>
            )}

        </Formik>
    );
};

export default CommentForm;
